import React, { useState, useRef, useEffect } from 'react';
import {
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaShieldAlt,
  FaUserCheck,
  FaBell,
  FaCreditCard,
  FaCheckDouble,
  FaUser,
  FaEnvelope,
  FaCertificate,
  FaRupeeSign,
  FaArrowRight,
  FaRedo,
  FaTimesCircle,
  FaMinusCircle,
  FaPhone,
  FaClock,
} from 'react-icons/fa';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { loadRazorpayScript } from '../utils/loadRazorpay';
import { submitForManualReview } from '../services/verificationReview';
import SecureModeConfirmModal from '../components/SecureModeConfirmModal';
import './NomineeCheckPage.css';
import Seo from '../components/Seo';

function parseAmountPaise(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function formatRupeeLabel(amountInPaise) {
  const rupees = amountInPaise / 100;
  return rupees.toLocaleString('en-IN', {
    maximumFractionDigits: rupees % 1 === 0 ? 0 : 2,
  });
}

function getChannelStepMeta(status, channel) {
  const isEmail = channel === 'email';
  if (status === 'SENT') {
    return {
      title: isEmail ? 'Email Delivered' : 'SMS Sent',
      description: isEmail
        ? 'Account holder and asset details sent to the nominee email address'
        : 'Instant notification delivered to the nominee phone number',
      icon: isEmail ? FaEnvelope : FaBell,
      color: isEmail ? '#3b82f6' : '#10b981',
      cardClass: 'step-card-success',
    };
  }
  if (status === 'NOT_APPLICABLE') {
    return {
      title: isEmail ? 'Email Not Applicable' : 'SMS Not Available',
      description: isEmail
        ? 'No nominee email address was available to notify'
        : 'SMS is not enabled yet — the nominee was notified by email only',
      icon: FaMinusCircle,
      color: '#94a3b8',
      cardClass: 'step-card-neutral',
    };
  }
  return {
    title: isEmail ? 'Email Failed' : 'SMS Failed',
    description: isEmail
      ? 'Could not deliver email to the nominee address'
      : 'Could not deliver SMS to the nominee phone number',
    icon: FaTimesCircle,
    color: '#ef4444',
    cardClass: 'step-card-failed',
  };
}

function NomineeLookupPage({ user }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState({ type: '', message: '' });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const toastRef = useRef();
  const [isSecureMode, setIsSecureMode] = useState(false);
  const [showSecureConfirm, setShowSecureConfirm] = useState(false);
  const [verificationCheckId, setVerificationCheckId] = useState(null);
  const [matchCount, setMatchCount] = useState(0);
  const [notificationResult, setNotificationResult] = useState(null);
  const [notificationSending, setNotificationSending] = useState(false);

  const baseAmountInPaise = parseAmountPaise(process.env.REACT_APP_NOMINEE_CHECK_BASE_AMOUNT_PAISE, 200000);
  const secureAmountInPaise = parseAmountPaise(process.env.REACT_APP_NOMINEE_CHECK_SECURE_AMOUNT_PAISE, 2000000);
  const skipNomineePayment = process.env.REACT_APP_SKIP_NOMINEE_PAYMENT === 'true';
  const effectiveAmountInPaise = isSecureMode ? secureAmountInPaise : baseAmountInPaise;
  const paymentAmountLabel = formatRupeeLabel(effectiveAmountInPaise);

  useEffect(() => {
    if (toast.message && toastRef.current) {
      toastRef.current.focus();
    }
  }, [toast]);

  const validateEmail = (value) => {
    if (!value) return '';
    if (value.length > 50) return "Email can't exceed more than 50 characters";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address.';
    return '';
  };

  const validatePhone = (value) => {
    if (!value) return '';
    if (!/^[0-9]{10}$/.test(value)) return 'Phone number must be exactly 10 digits.';
    if (value.startsWith('0') || value.startsWith('1')) return 'Phone number should not start with 0 or 1.';
    return '';
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
    }
  };

  const persistSecureModeOnCheck = async (checkId, secureModeRequired) => {
    const token = localStorage.getItem('token');
    if (!checkId) return;
    await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/nominee-verifications/${checkId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        secureMode: secureModeRequired,
        amountExpectedPaise: secureModeRequired ? secureAmountInPaise : baseAmountInPaise,
      }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast({ type: '', message: '' });

    const emailError = email ? validateEmail(email) : '';
    const phoneError = phone ? validatePhone(phone) : '';

    if (!email.trim() && !phone.trim()) {
      setToast({ type: 'error', message: 'Please enter nominee email or phone number.' });
      return;
    }

    setErrors({ email: emailError, phone: phoneError });

    if (emailError || phoneError) {
      setToast({ type: 'error', message: 'Please fix the errors in the form before submitting.' });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    if (email) formData.append('nomineeEmail', email);
    if (phone) formData.append('nomineePhone', phone);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/lookup/validate`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;
      if (data.successful === true) {
        const nextCheckId = data.data?.verificationCheckId ?? null;
        const secureModeRequired = Boolean(data.data?.secureModeRequired);
        const matches = Number(data.data?.matchCount || 0);
        setVerificationCheckId(nextCheckId);
        setMatchCount(matches);
        setIsSecureMode(secureModeRequired);
        if (secureModeRequired) setShowSecureConfirm(true);
        await persistSecureModeOnCheck(nextCheckId, secureModeRequired);
        setToast({
          type: 'success',
          message: `Found ${matches} matching record${matches === 1 ? '' : 's'}. Proceeding to payment…`,
        });
        setStep(2);
      } else {
        if (data.data?.verificationCheckId) {
          setVerificationCheckId(data.data.verificationCheckId);
        }
        setToast({
          type: 'error',
          message: data.message || 'No matching nominee records found.',
        });
      }
    } catch (error) {
      setToast({ type: 'error', message: 'Error calling lookup API. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const sendNomineeNotification = async () => {
    const token = localStorage.getItem('token');
    const emailRequestBody = {
      emailId: email || null,
      phoneNumber: phone || null,
      verificationCheckId: verificationCheckId || null,
      checkType: 'NOMINEE_LOOKUP',
    };

    setNotificationSending(true);
    try {
      const sendResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(emailRequestBody),
      });

      const json = await sendResponse.json().catch(() => ({}));
      const result = json.data && typeof json.data === 'object' ? json.data : null;

      if (!result?.emailStatus) {
        const notification = {
          emailStatus: 'FAILED',
          smsStatus: 'NOT_APPLICABLE',
          emailMessage: json.message || 'Email API did not return delivery status.',
          smsMessage: 'SMS notifications are not enabled yet.',
          overallSuccess: false,
        };
        setNotificationResult(notification);
        setToast({ type: 'error', message: notification.emailMessage });
        return notification;
      }

      const notification = {
        emailStatus: result.emailStatus,
        smsStatus: result.smsStatus || 'NOT_APPLICABLE',
        emailMessage: result.emailMessage || '',
        smsMessage: result.smsMessage || 'SMS notifications are not enabled yet.',
        overallSuccess: Boolean(result.overallSuccess),
      };
      setNotificationResult(notification);

      if (!notification.overallSuccess) {
        setToast({
          type: 'error',
          message: json.message || 'Payment verified but notification delivery failed.',
        });
      }

      return notification;
    } catch (err) {
      const failed = {
        emailStatus: 'FAILED',
        smsStatus: 'NOT_APPLICABLE',
        emailMessage: '',
        smsMessage: '',
        overallSuccess: false,
      };
      setNotificationResult(failed);
      setToast({
        type: 'error',
        message: 'Payment verified but error occurred sending notification.',
      });
      return failed;
    } finally {
      setNotificationSending(false);
    }
  };

  const completePaymentAndNotify = async (successMessage) => {
    setToast({ type: 'success', message: successMessage });
    let notification;
    if (isSecureMode) {
      setNotificationSending(true);
      try {
        notification = await submitForManualReview(verificationCheckId, localStorage.getItem('token'));
      } catch (err) {
        notification = {
          reviewPending: false,
          overallSuccess: false,
          emailStatus: 'FAILED',
          smsStatus: 'NOT_APPLICABLE',
          emailMessage: err.message || 'Payment received but manual review submission failed.',
          smsMessage: 'SMS notifications are not enabled yet.',
        };
        setToast({ type: 'error', message: notification.emailMessage });
      } finally {
        setNotificationSending(false);
      }
    } else {
      notification = await sendNomineeNotification();
    }
    setNotificationResult(notification);
    setStep(3);
  };

  const handlePayment = async (amountPaise) => {
    const token = localStorage.getItem('token');
    setPaymentLoading(true);
    setToast({ type: '', message: '' });

    if (skipNomineePayment) {
      try {
        const createOrderResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/orders/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            serviceType: 'nominee_check',
            pickupDate: new Date().toISOString(),
            amount: amountPaise,
            verificationCheckId,
          }),
        });

        if (!createOrderResponse.ok) {
          const errorText = await createOrderResponse.text();
          setToast({ type: 'error', message: 'Order creation failed: ' + errorText });
          setPaymentLoading(false);
          return;
        }

        const responseData = await createOrderResponse.json();
        if (responseData?.data?.skipPayment) {
          await completePaymentAndNotify('Payment skipped (local dev mode).');
        } else {
          setToast({
            type: 'info',
            message: 'Set nominee.check.skip-payment=true on the backend to skip Razorpay locally.',
          });
        }
      } catch (error) {
        setToast({ type: 'error', message: 'Payment failed: ' + error.message });
      } finally {
        setPaymentLoading(false);
      }
      return;
    }

    const res = await loadRazorpayScript();
    if (!res) {
      setToast({ type: 'error', message: 'Failed to load Razorpay SDK. Please check your internet connection.' });
      setPaymentLoading(false);
      return;
    }

    try {
      const createOrderResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          serviceType: 'nominee_check',
          pickupDate: new Date().toISOString(),
          amount: amountPaise,
          verificationCheckId,
        }),
      });

      if (!createOrderResponse.ok) {
        const errorText = await createOrderResponse.text();
        setToast({ type: 'error', message: 'Order creation failed: ' + errorText });
        setPaymentLoading(false);
        return;
      }

      const responseData = await createOrderResponse.json();
      const razorpayOrderId = responseData?.data?.razorpayOrderId;
      const currency = responseData?.data?.currency;
      const amount = responseData?.data?.amount;

      if (responseData?.data?.skipPayment) {
        await completePaymentAndNotify('Payment skipped (local dev mode).');
        setPaymentLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount,
        currency,
        name: 'KeepMyAsset',
        description: 'Nominee Lookup Payment',
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/orders/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            });

            if (verifyResponse.ok) {
              await completePaymentAndNotify('Payment successful and verified!');
            } else {
              setToast({ type: 'error', message: 'Payment verification failed.' });
            }
          } catch (err) {
            setToast({ type: 'error', message: 'Error verifying payment.' });
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setToast({ type: 'info', message: 'Payment popup closed.' });
            setPaymentLoading(false);
          },
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.contact,
        },
        theme: { color: '#3399cc' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setToast({ type: 'error', message: 'Payment failed: ' + error.message });
      setPaymentLoading(false);
    }
  };

  const handlePayClick = () => {
    if (isSecureMode) {
      setShowSecureConfirm(true);
      return;
    }
    handlePayment(effectiveAmountInPaise);
  };

  const handleReset = () => {
    setEmail('');
    setPhone('');
    setStep(1);
    setToast({ type: '', message: '' });
    setErrors({ email: '', phone: '' });
    setVerificationCheckId(null);
    setMatchCount(0);
    setNotificationResult(null);
    setNotificationSending(false);
    setIsSecureMode(false);
    setShowSecureConfirm(false);
  };

  return (
    <>
      {notificationSending && (
        <div className="notification-fullscreen-overlay" role="alert" aria-live="assertive" aria-busy="true">
          <div className="notification-fullscreen-content">
            <FaSpinner className="notification-fullscreen-spinner" aria-hidden="true" />
            <h2>{isSecureMode ? 'Submitting for manual review' : 'Sending notification'}</h2>
            <p>
              {isSecureMode
                ? 'Please wait while we queue this secure-mode lookup for admin verification…'
                : 'Please wait while we notify the nominee with account holder and asset details…'}
            </p>
          </div>
        </div>
      )}
      <Seo
        title="Nominee Lookup — Find Your Records"
        description="Look up nominee records by email or phone and receive account holder and asset details through KeepMyAsset."
        path="/nominee-lookup"
        keywords="nominee lookup, find nominee records, keepmyasset nominee search"
      />
      <div className="nominee-check-page">
        {step === 1 && (
          <section className="hero-section">
            <div className="hero-content">
              <h1 className="hero-title">
                <FaUser style={{ marginRight: '1rem', color: '#6366f1' }} />
                Nominee Lookup Portal
              </h1>
              <p className="hero-description">
                Enter a nominee&apos;s registered email or phone number to find linked asset records.
                After verification and payment, the nominee receives account holder names and asset details.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FaUserCheck className="hero-feature-icon" />
                  <h4>Nominee-Centered Search</h4>
                  <p>Find records using the nominee&apos;s own contact details</p>
                </div>
                <div className="hero-feature">
                  <FaShieldAlt className="hero-feature-icon" />
                  <h4>Verified Delivery</h4>
                  <p>Only the entered nominee contact receives the notification</p>
                </div>
                <div className="hero-feature">
                  <FaBell className="hero-feature-icon" />
                  <h4>Owner &amp; Asset Details</h4>
                  <p>Includes document owner name and linked provider records</p>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="search-results">
          <div className="results-container">
            <div className="results-header">
              <h2>
                <FaCertificate style={{ color: '#6366f1' }} />
                Nominee Lookup &amp; Notification
              </h2>
              <div className="step-indicator">Step {step} of 3</div>
            </div>

            {toast.message && (
              <div
                className={`toast-notification ${toast.type}`}
                tabIndex={-1}
                ref={toastRef}
                aria-live="assertive"
                role="alert"
              >
                {toast.type === 'error' && <FaExclamationTriangle />}
                {toast.type === 'success' && <FaCheckCircle />}
                {toast.type === 'info' && <FaInfoCircle />}
                {toast.message}
              </div>
            )}

            {step === 1 && (
              <div className="upload-form-container">
                <form onSubmit={handleSubmit} className="upload-form">
                  <div className="form-section">
                    <h3>
                      <FaUser style={{ color: '#6366f1' }} />
                      Nominee Details
                    </h3>
                    <p style={{ color: '#64748b', marginTop: '-6px', marginBottom: '12px' }}>
                      Enter the nominee&apos;s registered email or mobile number. If a match exists,
                      we will notify that nominee with the document owner name and linked assets.
                    </p>
                    <p style={{ color: '#64748b', marginBottom: '12px', fontSize: '0.92rem' }}>
                      Looking for all nominees under one account holder instead?{' '}
                      <Link to="/nominee-check">Use Notify All Nominees</Link>.
                    </p>

                    <div className="input-row-horizontal">
                      <div className="input-group">
                        <label>
                          <FaEnvelope style={{ color: '#6366f1' }} />
                          Nominee Email
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          placeholder="e.g. akash@example.com"
                          className={errors.email ? 'input-error' : ''}
                          maxLength={50}
                        />
                        {errors.email && (
                          <div className="error-message">
                            <FaExclamationTriangle />
                            {errors.email}
                          </div>
                        )}
                      </div>

                      <span className="or-separator" aria-hidden="true">or</span>

                      <div className="input-group">
                        <label>
                          <FaPhone style={{ color: '#6366f1' }} />
                          Nominee Phone
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          placeholder="10-digit mobile number"
                          className={errors.phone ? 'input-error' : ''}
                          maxLength={10}
                        />
                        {errors.phone && (
                          <div className="error-message">
                            <FaExclamationTriangle />
                            {errors.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <button type="submit" disabled={loading} className="submit-btn">
                    {loading ? (
                      <>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                        Searching records...
                      </>
                    ) : (
                      <>
                        <FaCheckDouble />
                        Lookup &amp; Proceed
                        <FaArrowRight />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {step === 2 && (
              <div className="payment-container">
                <div style={{ marginBottom: '3rem' }}>
                  <FaCreditCard style={{ fontSize: '5rem', color: '#6366f1', marginBottom: '1.5rem' }} />
                  <h3>Secure Payment Required</h3>
                  <p>
                    Complete payment to notify the nominee at{' '}
                    <strong>{email || phone}</strong>
                    {matchCount > 0 ? ` (${matchCount} matching record${matchCount === 1 ? '' : 's'})` : ''}
                  </p>
                </div>
                <div className="payment-amount">
                  <FaRupeeSign />
                  Payment Amount: ₹{paymentAmountLabel}
                  {skipNomineePayment ? ' (dev: payment skipped)' : ''}
                </div>
                <button
                  onClick={handlePayClick}
                  disabled={paymentLoading}
                  className="submit-btn"
                  style={{ maxWidth: '500px', margin: '0 auto' }}
                >
                  {paymentLoading ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      Processing Payment...
                    </>
                  ) : (
                    <>
                      <FaCreditCard />
                      {skipNomineePayment ? 'Continue (skip payment)' : `Pay ₹${paymentAmountLabel} Securely`}
                      <FaArrowRight />
                    </>
                  )}
                </button>
              </div>
            )}

            {step === 3 && (
              <div className="success-container">
                <div className="success-message">
                  {notificationResult?.reviewPending ? (
                    <FaShieldAlt className="success-icon" style={{ color: '#2563eb' }} />
                  ) : notificationResult?.overallSuccess ? (
                    <FaCheckCircle className="success-icon" />
                  ) : (
                    <FaExclamationTriangle className="success-icon" style={{ color: '#ef4444' }} />
                  )}
                  <h3>
                    {notificationResult?.reviewPending
                      ? 'Submitted for Manual Review'
                      : notificationResult?.overallSuccess
                        ? 'Notification Sent!'
                        : 'Payment Complete — Notification Issues'}
                  </h3>
                  <p>
                    {notificationResult?.reviewPending
                      ? 'Secure Mode applies to linked account holder(s). KeepMyAsset will manually verify before sharing document details with the requester.'
                      : notificationResult?.overallSuccess
                        ? 'The nominee has been notified with account holder and asset details.'
                        : 'Payment was completed, but notification delivery failed. Review the status below.'}
                  </p>
                </div>
                <div className="success-steps">
                  {notificationResult?.reviewPending ? (
                    <>
                      <div className="step-card step-card-review">
                        <FaShieldAlt className="step-icon" style={{ color: '#2563eb' }} />
                        <h4>Awaiting admin review</h4>
                        <p>{notificationResult.emailMessage}</p>
                      </div>
                      <div className="step-card step-card-review">
                        <FaClock className="step-icon" style={{ color: '#f59e0b' }} />
                        <h4>Track in Verification History</h4>
                        <p>Status will show as &quot;Awaiting manual review&quot; until an admin approves or rejects the request.</p>
                      </div>
                    </>
                  ) : (
                    <>
                  {['sms', 'email'].map((channel) => {
                    const status = notificationResult?.[`${channel}Status`] || 'FAILED';
                    const meta = getChannelStepMeta(status, channel);
                    const StepIcon = meta.icon;
                    const detailMessage = notificationResult?.[`${channel}Message`];
                    return (
                      <div key={channel} className={`step-card ${meta.cardClass}`}>
                        <StepIcon className="step-icon" style={{ color: meta.color }} />
                        <h4>{meta.title}</h4>
                        <p>{detailMessage || meta.description}</p>
                      </div>
                    );
                  })}
                    </>
                  )}
                </div>
                <button
                  onClick={handleReset}
                  className="submit-btn"
                  style={{ width: 'auto', display: 'inline-flex', maxWidth: '400px', margin: '0 auto' }}
                >
                  <FaRedo />
                  Start Another Lookup
                </button>
              </div>
            )}
          </div>
        </section>

        <SecureModeConfirmModal
          isOpen={showSecureConfirm}
          onCancel={() => { setShowSecureConfirm(false); handleReset(); }}
          onConfirm={() => { setShowSecureConfirm(false); handlePayment(secureAmountInPaise); }}
          baseAmountInPaise={baseAmountInPaise}
          secureAmountInPaise={secureAmountInPaise}
          description="One or more linked account holders have Secure Mode enabled. Additional manual verification applies before the nominee is notified."
        />
      </div>
    </>
  );
}

export default NomineeLookupPage;
